import React, { useState, useRef, useEffect, useCallback } from "react";
import "../Css/About.css";
import CoreTeamData from "./CoreTeamData";
import AdvisoryBoardData from "./AdvisoryBoardData";
import USNationals2026OutReach2 from "../Assets/USNationals2026OutReach-2.jpeg";

// ─── Inline icons ──────────────────────────────────────────────
const ArrowIcon = ({ direction = "right" }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ transform: direction === "left" ? "rotate(180deg)" : "none" }}
  >
    <path d="M9 6l6 6-6 6" />
  </svg>
);

const CloseIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

// ─── Shared 4-column team row ───────────────────────────────────
// Every row on the page uses the SAME grid: 4 equal slots, each slot =
// [rotated label lane][card]. A chapter's label sits in the lane of the
// slot where that chapter starts; other lanes stay empty. Because every
// row has identical column geometry, all rows are the same length and
// every card is the same size, regardless of how chapters are combined.
const ROW_SLOTS = 4;

const TeamRow = ({ groups, onSelectMember, ariaLabel }) => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < maxScroll - 4);
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [updateScrollState]);

  const scrollBy = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const slotWidth = el.querySelector(".ct-row__slot")?.offsetWidth || 240;
    el.scrollBy({ left: dir * slotWidth, behavior: "smooth" });
  };

  // Flatten groups into slots; the label goes before member `labelAt` (default 0).
  // `spanLabel` drops the rotated label to the midpoint of two stacked rows,
  // so a group that wraps (more members than ROW_SLOTS) is labelled as a block.
  const slots = [];
  groups.forEach((g) => {
    const labelAt = g.labelAt ?? 0;
    // `centerTail`: when a group wraps and leaves a single card on the
    // last row, centre that card across the row instead of parking it
    // in column 1.
    const centerAt =
      g.centerTail && g.members.length > ROW_SLOTS && g.members.length % ROW_SLOTS === 1
        ? g.members.length - 1
        : -1;
    g.members.forEach((m, i) => {
      slots.push({
        member: m,
        groupLabel: g.groupLabel,
        label: i === labelAt ? g.label : null,
        spanLabel: i === labelAt && !!g.spanLabel,
        center: i === centerAt,
      });
    });
  });

  return (
    <section className="ct-row" aria-label={ariaLabel}>
      {canScrollLeft && (
        <button
          type="button"
          className="ct-arrow ct-arrow--left"
          onClick={() => scrollBy(-1)}
          aria-label={`Scroll ${ariaLabel} left`}
        >
          <ArrowIcon direction="left" />
        </button>
      )}

      <div className="ct-row__track" ref={scrollRef}>
        {slots.map((s, i) => (
          <div
            className={`ct-row__slot${s.center ? " ct-row__slot--center" : ""}`}
            key={i}
          >
            <div
              className={`ct-row__label${s.spanLabel ? " ct-row__label--span" : ""}`}
              aria-hidden="true"
            >
              {s.label && <span className="ct-row__label-text">{s.label}</span>}
            </div>
            <button
              type="button"
              className="ct-card ct-row__card"
              onClick={() => onSelectMember({ ...s.member, groupLabel: s.groupLabel })}
              aria-label={`View details for ${s.member.name}`}
            >
              <div className="ct-card__photo-wrap">
                <img src={s.member.photo} alt={s.member.name} className="ct-card__photo" loading="lazy" />
              </div>
              <div className="ct-card__name">{s.member.name}</div>
              <div className="ct-card__role">{s.member.role}</div>
            </button>
          </div>
        ))}
      </div>

      {canScrollRight && (
        <button
          type="button"
          className="ct-arrow ct-arrow--right"
          onClick={() => scrollBy(1)}
          aria-label={`Scroll ${ariaLabel} right`}
        >
          <ArrowIcon direction="right" />
        </button>
      )}
    </section>
  );
};

// Look up a chapter and shape it as a TeamRow group
const chapterGroup = (name, opts = {}) => {
  const c = CoreTeamData.find((x) => x.chapter === name);
  if (!c) return null;
  return { label: c.chapter, members: c.members, groupLabel: `${c.chapter} Chapter`, ...opts };
};

// Row layout — each row totals ROW_SLOTS (4) members
const TEAM_ROWS = [
  [chapterGroup("Seattle, WA", { labelAt: 1 })],
  [chapterGroup("Los Angeles, CA"), chapterGroup("Tri-Valley, CA")],
  [chapterGroup("Milpitas, CA")],
  [chapterGroup("New Jersey"), chapterGroup("San Diego, CA")],
  [chapterGroup("Fremont, CA"), chapterGroup("Houston, TX")],
  [chapterGroup("San Jose, CA"), chapterGroup("Phoenix, AZ")],
].map((row) => row.filter(Boolean));

// ─── Modal: detailed view of any member ─────────────────────────
const MemberModal = ({ member, onClose }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  if (!member) return null;

  return (
    <div className="ct-modal-overlay" onClick={onClose} role="presentation">
      <div
        className="ct-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`${member.name} — full bio`}
      >
        <button
          type="button"
          className="ct-modal__close"
          onClick={onClose}
          aria-label="Close detail view"
        >
          <CloseIcon />
        </button>
        <div className="ct-modal__photo-wrap">
          <img src={member.photo} alt={member.name} className="ct-modal__photo" />
        </div>
        <div className="ct-modal__body">
          <div className="ct-modal__chapter">{member.groupLabel}</div>
          <h2 className="ct-modal__name">{member.name}</h2>
          <div className="ct-modal__role">{member.role}</div>
          <div className="ct-modal__bio">
            {member.bio.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Page ──────────────────────────────────────────────────────
const About = () => {
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = "SportsForEquity | Empowering Neuro Wellness And Health Equity Using Table Tennis";

    let metaDescription = document.querySelector('meta[name="description"]');
    let createdMeta = false;
    const prevDescription = metaDescription ? metaDescription.getAttribute("content") : null;

    if (!metaDescription) {
      metaDescription = document.createElement("meta");
      metaDescription.setAttribute("name", "description");
      document.head.appendChild(metaDescription);
      createdMeta = true;
    }
    metaDescription.setAttribute(
      "content",
      "SportsForEquity is a youth-led organization using table tennis to advance health equity for the Parkinson's community, seniors, and young athletes nationwide."
    );

    const personSchema = document.createElement("script");
    personSchema.type = "application/ld+json";
    personSchema.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Person",
      "name": "Saicharan Vellanki",
      "jobTitle": "Founder & National President",
      "worksFor": {
        "@type": "Organization",
        "name": "SportsForEquity",
        "url": "https://sportsforequity.live/"
      },
      "url": "https://sportsforequity.live/about",
      "sameAs": [
        "https://www.linkedin.com/company/sportsforequityorg"
      ]
    });
    document.head.appendChild(personSchema);

    return () => {
      document.title = prevTitle;
      if (createdMeta) {
        metaDescription.remove();
      } else if (prevDescription !== null) {
        metaDescription.setAttribute("content", prevDescription);
      }
      personSchema.remove();
    };
  }, []);

  return (
    <div className="about-wrapper">
      <div className="about-hero">
        <img
          src={USNationals2026OutReach2}
          alt="SportsForEquity team at the 2026 U.S. National Table Tennis Championships"
          className="about-hero-img"
        />
      </div>

      <div className="coreteam-container">
      {/* ─── Founding Story ─── */}
      <h2 className="coreteam-title">Founding Story</h2>
      <div className="founding-story">
        <p className="founding-story-text">
          SportsForEquity was founded by Saicharan Vellanki, National President, in Seattle, Washington, with a mission to use table tennis to advance community neuro-wellness and public-health equity.
        </p>
        <p className="founding-story-text">
          What began as a local initiative has grown into a national, youth-athlete-led network, with community collaborations including the global PingPongParkinson® network, the American Parkinson Disease Association Northwest Chapter, and the Northwest Parkinson's Foundation. SportsForEquity has also developed a referral network connecting physical therapists, Parkinson's specialists, and community organizations with its programs.
        </p>
        <p className="founding-story-text">
          The organization's work has been recognized through state and national platforms, including the American Public Health Association (APHA). Across its chapters, youth athlete leaders expand Parkinson's neuro-wellness sessions, community and care-network outreach, accessible table-tennis programming, civic engagement, advocacy, and fundraising for health initiatives. Through this model, SportsForEquity empowers young athletes to become future leaders and community advocates.
        </p>
      </div>

      {/* ─── National Network (Advisory Board + Core Team chapters) ─── */}
      <h2 className="coreteam-title" id="national-network-section">Team</h2>
      <div className="ct-rows" style={{ "--ct-row-slots": ROW_SLOTS }}>
        {/* Advisory Board first, then chapters */}
        {AdvisoryBoardData.map((c) => (
          <TeamRow
            key={c.category}
            ariaLabel={c.category}
            groups={[
              {
                label: c.category,
                members: c.members,
                groupLabel: c.category,
                // Wraps to a 2nd row → centre the label across both rows
                spanLabel: c.members.length > ROW_SLOTS,
                // …and centre a lone card left over on that 2nd row
                centerTail: true,
              },
            ]}
            onSelectMember={setSelected}
          />
        ))}
        {TEAM_ROWS.map((groups) => (
          <TeamRow
            key={groups.map((g) => g.label).join("|")}
            ariaLabel={groups.map((g) => g.label).join(", ")}
            groups={groups}
            onSelectMember={setSelected}
          />
        ))}
      </div>

      {/* Single shared modal */}
      {selected && <MemberModal member={selected} onClose={() => setSelected(null)} />}
      </div>
    </div>
  );
};

export default About;